import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import LinkIcon from '@mui/icons-material/Link';
import TitleIcon from '@mui/icons-material/Title';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Divider, IconButton, Stack, TextField, Tooltip, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useRef, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { ICON_SIZE } from './LabelWithIcon';

interface MarkdownFieldProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Renders only the formatted markdown — no edit/preview toggle, no source textarea. Use
   *  this for read-only display contexts (a project/issue detail view, a note/comment list);
   *  the edit/preview toggle is only meaningful while actually editing. `onChange` is never
   *  called in this mode. */
  readOnly?: boolean;
  /** When provided (together with `onCancel`), renders Confirm (tick) and Cancel icon buttons
   *  at the right end of the helper-text row, instead of the caller having to render its own
   *  separate Save/Cancel controls below the field — for an inline "click to edit, confirm or
   *  cancel in place" usage (e.g. an issue's Description). Omit both for a plain field inside a
   *  form that already has its own Save/Cancel elsewhere (the common case). */
  onConfirm?: () => void;
  onCancel?: () => void;
  /** Disables the Confirm icon button — e.g. while the value is over its character limit. */
  confirmDisabled?: boolean;
  stickyLabel?: boolean;
}

const CONTENT_SX = {
  '& > :first-of-type': { mt: 0 },
  '& > :last-child': { mb: 0 },
  wordBreak: 'break-word',
} as const;

// Wraps (or inserts at the cursor, if nothing's selected) the current TextField selection with
// markdown syntax — the same interaction every visual markdown toolbar (GitHub, Jira, etc.)
// uses. `linePrefix` variants (heading/list/quote) apply per-selected-line instead of wrapping.
type ToolbarAction =
  | { kind: 'wrap'; before: string; after: string; placeholder: string }
  | { kind: 'linePrefix'; prefix: string };

function applyToolbarAction(textarea: HTMLTextAreaElement, action: ToolbarAction, value: string): { next: string; selectionStart: number; selectionEnd: number } {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selected = value.slice(start, end);

  if (action.kind === 'wrap') {
    const text = selected || action.placeholder;
    const next = value.slice(0, start) + action.before + text + action.after + value.slice(end);
    const selectionStart = start + action.before.length;
    const selectionEnd = selectionStart + text.length;
    return { next, selectionStart, selectionEnd };
  }

  // linePrefix — apply to every line touched by the selection (or the current line, if collapsed).
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const lastNewlineInSelection = value.lastIndexOf('\n', Math.max(end - 1, lineStart));
  const lineEnd = lastNewlineInSelection >= lineStart ? lastNewlineInSelection : value.indexOf('\n', end);
  const effectiveLineEnd = lineEnd === -1 ? value.length : lineEnd;
  const block = value.slice(lineStart, effectiveLineEnd);
  const lines = block.split('\n');
  const prefixed = lines.map((line, i) => (action.prefix === '1. ' ? `${i + 1}. ${line}` : `${action.prefix}${line}`)).join('\n');
  const next = value.slice(0, lineStart) + prefixed + value.slice(effectiveLineEnd);
  return { next, selectionStart: lineStart, selectionEnd: lineStart + prefixed.length };
}

interface ToolbarButtonDef {
  label: string;
  icon: ReactNode;
  action: ToolbarAction;
}

const TOOLBAR_BUTTONS: ToolbarButtonDef[] = [
  { label: 'Bold', icon: <FormatBoldIcon fontSize="small" />, action: { kind: 'wrap', before: '**', after: '**', placeholder: 'bold text' } },
  { label: 'Italic', icon: <FormatItalicIcon fontSize="small" />, action: { kind: 'wrap', before: '*', after: '*', placeholder: 'italic text' } },
  { label: 'Strikethrough', icon: <FormatStrikethroughIcon fontSize="small" />, action: { kind: 'wrap', before: '~~', after: '~~', placeholder: 'strikethrough text' } },
  { label: 'Heading', icon: <TitleIcon fontSize="small" />, action: { kind: 'linePrefix', prefix: '## ' } },
  { label: 'Bulleted list', icon: <FormatListBulletedIcon fontSize="small" />, action: { kind: 'linePrefix', prefix: '- ' } },
  { label: 'Numbered list', icon: <FormatListNumberedIcon fontSize="small" />, action: { kind: 'linePrefix', prefix: '1. ' } },
  { label: 'Quote', icon: <FormatQuoteIcon fontSize="small" />, action: { kind: 'linePrefix', prefix: '> ' } },
  { label: 'Code', icon: <CodeIcon fontSize="small" />, action: { kind: 'wrap', before: '`', after: '`', placeholder: 'code' } },
  { label: 'Link', icon: <LinkIcon fontSize="small" />, action: { kind: 'wrap', before: '[', after: '](url)', placeholder: 'link text' } },
];

// Every "Markdown" field in the spec (§1) renders through this one component. Editing gets a
// formatting toolbar + edit/preview toggle over a plain multiline TextField (source) and
// react-markdown+remark-gfm (preview); viewing (readOnly) just renders the formatted markdown
// directly.
export function MarkdownField({
  label,
  value,
  onChange,
  rows = 4,
  required,
  error,
  helperText,
  disabled,
  readOnly,
  onConfirm,
  onCancel,
  confirmDisabled,
  stickyLabel,
}: MarkdownFieldProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  if (readOnly) {
    return (
      <Box>
        {label && (
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{
              ...(stickyLabel ? { position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' } : {}),
            }}
          >
            {label}
          </Typography>
        )}
        {value.trim().length > 0 ? (
          <Box sx={{ minHeight: (t) => t.spacing(rows * 3), ...CONTENT_SX }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ minHeight: (t) => t.spacing(rows * 3) }}>
            —
          </Typography>
        )}
      </Box>
    );
  }

  function handleToolbarClick(action: ToolbarAction) {
    const textarea = textareaRef.current;
    if (!textarea || disabled) return;
    const { next, selectionStart, selectionEnd } = applyToolbarAction(textarea, action, value);
    onChange(next);
    // Restore focus + selection after React re-renders the (now longer/shorter) value.
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(selectionStart, selectionEnd);
    });
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: 0.5,
          ...(stickyLabel ? { position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' } : {}),
        }}
      >
        <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
          {label}
          {required ? ' *' : ''}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_e, next: 'edit' | 'preview' | null) => next && setMode(next)}
          aria-label="Markdown edit or preview"
          sx={{ '& .MuiToggleButton-root': { py: 0.25 } }}
        >
          <ToggleButton value="edit" aria-label="Edit">
            <EditIcon sx={{ fontSize: ICON_SIZE.xs }} />
          </ToggleButton>
          <ToggleButton value="preview" aria-label="Preview">
            <VisibilityIcon sx={{ fontSize: ICON_SIZE.xs }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {mode === 'edit' ? (
        <Box sx={{ border: '1px solid', borderColor: error ? 'error.main' : 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            sx={{ px: 0.5, py: 0, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {TOOLBAR_BUTTONS.map((btn) => (
              <Tooltip key={btn.label} title={btn.label}>
                <span>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleToolbarClick(btn.action)}
                    aria-label={btn.label}
                    sx={{ p: 0.5 }}
                  >
                    {btn.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          </Stack>
          <TextField
            inputRef={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            multiline
            rows={rows}
            maxRows={rows}
            disabled={disabled}
            variant="standard"
            InputProps={{ disableUnderline: true, sx: { px: 1.5, py: 0 } }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            minHeight: (t) => t.spacing(rows * 4),
            maxHeight: (t) => t.spacing(rows * 4),
            overflowY: 'auto',
            ...CONTENT_SX,
          }}
        >
          {value.trim().length > 0 ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <Typography variant="body2" color="text.disabled">
              Nothing to preview.
            </Typography>
          )}
        </Box>
      )}
      {(helperText || onConfirm || onCancel) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ ml: 1.75 }}>
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
            {helperText}
          </Typography>
          {(onConfirm || onCancel) && (
            <Stack direction="row" spacing={0.25}>
              {onConfirm && (
                <IconButton size="small" color="success" onClick={onConfirm} disabled={confirmDisabled} aria-label="Confirm">
                  <CheckIcon fontSize="small" />
                </IconButton>
              )}
              {onCancel && (
                <IconButton size="small" onClick={onCancel} aria-label="Cancel">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}
